import React, { useEffect, ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { Switch, Route } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useDispatch } from 'react-redux';
import breakpoints, { forDesktop } from '@westwing/ui-kit/breakPoints';
import fontStyles from './GlobalStyles/Fonts';
import Normalizer from './GlobalStyles/Normalizer';
import ClubIntegrationPixel from './ThirdPartyIntegration/ClubIntegrationPixel';
import theme, { paddedMaxScreenWidth } from './GlobalStyles/theme';
import ErrorBoundary from './Generic/ErrorBoundaries';
import NotFound from './NotFound';
import RouteScrollToTop from './RouteScrollToTop';
import AppHeader from './AppHeader';
import useHotjar from './ThirdPartyIntegration/useHotjar';
import useAppTracking from './useAppTracking';
import OneFooter from './OneFooter';
import paths from 'Common/routes/paths';
import routes, { RouteInterface } from 'Common/routes';
import appleTouchIcon from 'Client/assets/favicons/apple-touch-icon.png';
import favIcon16 from 'Client/assets/favicons/favicon-16x16.png';
import favIcon32 from 'Client/assets/favicons/favicon-32x32.png';
import favIconSafari from 'Client/assets/favicons/safari-pinned-tab.svg';
import { RouterProvider } from 'Client/hooks/useRouter';
import { UserTrackingProvider } from 'Client/hooks/flags/useUserTracking';
import { addNotification } from 'Client/redux/notifications/actions';
import { cookiesEnabled } from 'Common/helpers/environment';
import useTranslator from 'Client/hooks/useTranslator';
import useConfig from 'Client/hooks/useConfig';
import { isNativeAppPage } from 'Client/hooks/UrlManager/pageTypeService';
import useSegments from 'Client/hooks/useSegments';

interface AppProps {
    host?: string;
    header: string | ReactNode;
}

const StyledContentWrapper = styled.div<{ fullWidth?: boolean }>`
    min-height: 100vh;
    position: relative;
    max-width: ${breakpoints.breakpointSemiWideScreens};
    ${({ fullWidth }) => css`
        ${forDesktop`
            max-width: ${fullWidth ? '100%' : paddedMaxScreenWidth}
        `}
    `}
    margin: auto;
`;

const App = ({ host = '', header }: AppProps) => {
    const dispatch = useDispatch();
    const t = useTranslator();
    const { trackAppWideVariations } = useAppTracking();
    const { env } = useConfig();
    let { staticHost } = useConfig();
    const segments = useSegments();
    if (env === 'local') {
        staticHost = '';
    }

    useHotjar();
    useEffect(() => {
        if (!cookiesEnabled) {
            const enableCookiesWarning = t(
                'In your browser, the cookies are not activated, which means that you can not use our site seamlessly. Please activate the cookies in the settings of your browser to use our website.'
            );
            dispatch(addNotification({ message: enableCookiesWarning, displayType: 'warning', isSticky: true }));
        }
    }, [dispatch, t]);

    useEffect(() => {
        trackAppWideVariations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ErrorBoundary boundaryName="App" fallbackComponent={<NotFound />}>
            <div className="pageContainer">
                <>
                    <Helmet style={[{ cssText: fontStyles(staticHost) }]}>
                        <title>WestwingNow</title>
                        <link rel="apple-touch-icon" sizes="180x180" href={appleTouchIcon} />
                        <link rel="icon" type="image/png" sizes="32x32" href={favIcon32} />
                        <link rel="icon" type="image/png" sizes="16x16" href={favIcon16} />
                        <link rel="mask-icon" href={favIconSafari} color={theme.colors.turquoise100} />
                        <link rel="manifest" href="/manifest.json" />
                    </Helmet>
                    <Normalizer />
                    <Route>
                        {routerProps => (
                            <RouterProvider
                                value={{
                                    ...routerProps,
                                    host,
                                    disableClientsideNavigation: isNativeAppPage(
                                        routerProps.location.pathname,
                                        segments
                                    ),
                                    isAppWebviewPage: isNativeAppPage(routerProps.location.pathname, segments),
                                }}
                            >
                                <RouteScrollToTop />
                                <UserTrackingProvider>
                                    {header}
                                    <div className="page">
                                        <StyledContentWrapper
                                            fullWidth={routerProps.location.pathname.startsWith(paths.CHECKOUT)}
                                        >
                                            <Switch>
                                                {routes.map((route: RouteInterface) => (
                                                    <Route
                                                        key={JSON.stringify(route.path)}
                                                        exact={route.exact}
                                                        path={route.path}
                                                        render={props => {
                                                            if (props.staticContext) {
                                                                // `status` is user defined key, which we use
                                                                // to setup complete https request handling in express app.
                                                                // @ts-ignore
                                                                // eslint-disable-next-line no-param-reassign
                                                                props.staticContext.status = route.code;
                                                            }
                                                            return <route.component {...props} />;
                                                        }}
                                                    />
                                                ))}
                                            </Switch>
                                        </StyledContentWrapper>
                                        <ErrorBoundary boundaryName="Footer" fallbackComponent={null}>
                                            <OneFooter />
                                        </ErrorBoundary>
                                        <ClubIntegrationPixel />
                                    </div>
                                </UserTrackingProvider>
                            </RouterProvider>
                        )}
                    </Route>
                </>
            </div>
        </ErrorBoundary>
    );
};

export function InitSSR({ host = '' }: { host?: string }) {
    return <App host={host} header={<AppHeader host={host} />} />;
}

export default App;
